import React from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { Mail, Shield, Calendar, CreditCard, Edit, ChevronRight } from 'lucide-react';

/**
 * Profile settings entry point (iter 73 cream restyle).
 *
 * Lightweight account-info surface - name, email, role badge, trial expiry,
 * plus quick links to "Edit your public profile" and "Manage subscription".
 * Replaces the previous dark-mode card layout.
 */
const PortalProfile = () => {
  const { user } = useAuth();

  const ROLE_BADGES = {
    free_trial:       { label: 'Free Trial',         tone: 'bg-[#F4ECD8] text-[#1A5C6E]' },
    blueprint_only:   { label: 'Blueprint Only',     tone: 'bg-[#F4ECD8] text-[#1A5C6E]' },
    essentials:       { label: 'Essentials',         tone: 'bg-[#2CA8A0]/15 text-[#1A5C6E]' },
    guided:           { label: 'Guided',             tone: 'bg-[#E8A830]/20 text-[#7E5A12]' },
    community:        { label: 'Community',          tone: 'bg-[#2CA8A0]/20 text-[#1A5C6E]' },
    connected:        { label: 'Connected',          tone: 'bg-[#2CA8A0]/30 text-[#0A1F2E]' },
    community_member: { label: 'Community',          tone: 'bg-[#2CA8A0]/20 text-[#1A5C6E]' },
    client:           { label: 'Client',             tone: 'bg-[#2CA8A0]/20 text-[#1A5C6E]' },
    admin:            { label: 'Admin',              tone: 'bg-[#0A1F2E] text-white' },
    expired_trial:    { label: 'Trial Expired',      tone: 'bg-[#EFE6CF] text-[#0A1F2E]/55' },
  };
  const badge = ROLE_BADGES[user?.role] || { label: 'Member', tone: 'bg-[#F4ECD8] text-[#1A5C6E]' };

  return (
    <PortalLayout activeTab="profile">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14" data-testid="portal-profile-page">
        <header className="mb-8">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
            Account
          </p>
          <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05]">
            Your profile
          </h1>
          <p className="font-body text-[#0A1F2E]/70 mt-2">
            Manage how you appear to other Mundalo members and review your account details.
          </p>
        </header>

        {/* Identity card */}
        <section className="bg-white border border-[#EFE6CF] rounded-2xl p-5 sm:p-6 mb-5" data-testid="profile-identity-card">
          <div className="flex items-start gap-4">
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-16 h-16 rounded-full object-cover ring-2 ring-[#EFE6CF] shrink-0"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#2CA8A0] text-white font-semibold flex items-center justify-center text-2xl shrink-0">
                {(user?.first_name?.[0] || user?.email?.[0] || 'M').toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="font-display italic text-2xl text-[#0A1F2E] truncate" data-testid="profile-name">
                {user?.first_name || ''} {user?.last_name || ''}
              </h2>
              <span
                className={`mt-1.5 inline-block font-mono uppercase text-[10px] tracking-[0.15em] px-2 py-0.5 rounded-full ${badge.tone}`}
                data-testid="profile-role-badge"
              >
                {badge.label}
              </span>
            </div>
            {user?.id && (
              <Link
                to="/portal/profile/edit"
                className="hidden sm:inline-flex items-center gap-1.5 font-mono uppercase text-[10px] tracking-[0.15em] bg-[#0A1F2E] hover:bg-[#163448] text-white px-4 py-2 rounded-full transition"
                data-testid="profile-edit-link-top"
              >
                <Edit className="w-3.5 h-3.5" /> Edit
              </Link>
            )}
          </div>

          {user?.id && (
            <Link
              to="/portal/profile/edit"
              className="sm:hidden mt-4 inline-flex items-center justify-center gap-1.5 w-full font-mono uppercase text-[10px] tracking-[0.15em] bg-[#0A1F2E] hover:bg-[#163448] text-white px-4 py-2.5 rounded-full transition"
              data-testid="profile-edit-link-mobile"
            >
              <Edit className="w-3.5 h-3.5" /> Edit your public profile
            </Link>
          )}
        </section>

        {/* Details list */}
        <section className="bg-white border border-[#EFE6CF] rounded-2xl divide-y divide-[#EFE6CF] mb-5" data-testid="profile-details-card">
          <div className="flex items-center gap-3 px-5 py-4">
            <Mail className="w-4 h-4 text-[#1A5C6E] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">Email</p>
              <p className="text-[#0A1F2E] text-sm truncate">{user?.email || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 px-5 py-4">
            <Shield className="w-4 h-4 text-[#1A5C6E] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">Account type</p>
              <p className="text-[#0A1F2E] text-sm capitalize">{(user?.role || 'member').replace(/_/g, ' ')}</p>
            </div>
          </div>
          {user?.trial_expiry_date && (
            <div className="flex items-center gap-3 px-5 py-4">
              <Calendar className="w-4 h-4 text-[#1A5C6E] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">Trial ends</p>
                <p className="text-[#0A1F2E] text-sm">
                  {new Date(user.trial_expiry_date).toLocaleDateString('en-US', {
                    month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago'})}
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Quick links */}
        <section className="bg-white border border-[#EFE6CF] rounded-2xl divide-y divide-[#EFE6CF]" data-testid="profile-actions-card">
          {user?.id && (
            <Link
              to={`/portal/members/${user.id}`}
              className="flex items-center gap-3 px-5 py-4 hover:bg-[#F9F6EF] transition"
              data-testid="profile-view-public-link"
            >
              <div className="flex-1">
                <p className="text-[#0A1F2E] text-sm font-medium">View your public profile</p>
                <p className="text-[#0A1F2E]/55 text-xs mt-0.5">See how other members see you</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#1A5C6E]/60" />
            </Link>
          )}
          <Link
            to="/portal/subscription"
            className="flex items-center gap-3 px-5 py-4 hover:bg-[#F9F6EF] transition"
            data-testid="profile-subscription-link"
          >
            <CreditCard className="w-4 h-4 text-[#1A5C6E]" />
            <div className="flex-1">
              <p className="text-[#0A1F2E] text-sm font-medium">Manage subscription</p>
              <p className="text-[#0A1F2E]/55 text-xs mt-0.5">Update your plan and billing</p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#1A5C6E]/60" />
          </Link>
        </section>
      </div>
    </PortalLayout>
  );
};

export default PortalProfile;
